/**
 * GDPR Cookie Consent Banner
 *
 * Shows a consent banner on first visit. Allows Accept All, Reject All, and granular Settings.
 * Uses the window.cookieconsent helper injected in index.html for persistence.
 */
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';

import css from './CookieConsentBanner.module.css';

const TEMPORARY_HIDE_KEY = 'cookieconsent-banner-hidden';

const readTemporaryHideState = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage?.getItem(TEMPORARY_HIDE_KEY) === 'true';
  } catch (error) {
    console.error('Cookie banner temporary hide state could not be read.', error);
    return false;
  }
};

const writeTemporaryHideState = isHidden => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (isHidden) {
      window.sessionStorage?.setItem(TEMPORARY_HIDE_KEY, 'true');
    } else {
      window.sessionStorage?.removeItem(TEMPORARY_HIDE_KEY);
    }
  } catch (error) {
    console.error('Cookie banner temporary hide state could not be saved.', error);
  }
};

const CookieConsentBanner = () => {
  const intl = useIntl();
  const [bannerVisible, setBannerVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);
  const [temporarilyHidden, setTemporarilyHidden] = useState(false);

  useEffect(() => {
    // Show banner only if no consent has been given yet
    if (typeof window !== 'undefined' && window.cookieconsent) {
      const hasConsented = window.cookieconsent.hasConsented();
      const isTemporarilyHidden = !hasConsented && readTemporaryHideState();

      setTemporarilyHidden(isTemporarilyHidden);
      setBannerVisible(!hasConsented && !isTemporarilyHidden);
    }

    // Listen for openPreferences event (from footer button)
    const handleOpenPrefs = () => {
      writeTemporaryHideState(false);
      setTemporarilyHidden(false);
      setSettingsOpen(true);
      setBannerVisible(true);
      // Read current consent state
      const consent = window.cookieconsent?.getConsent?.();
      setAnalyticsEnabled(consent?.analytics === true);
      setMarketingEnabled(consent?.marketing === true);
    };

    window.addEventListener('cookieconsent:openPreferences', handleOpenPrefs);
    return () => {
      window.removeEventListener('cookieconsent:openPreferences', handleOpenPrefs);
    };
  }, []);

  const handleAcceptAll = () => {
    window.cookieconsent?.acceptAll?.();
    writeTemporaryHideState(false);
    setTemporarilyHidden(false);
    setBannerVisible(false);
    setSettingsOpen(false);
  };

  const handleRejectAll = () => {
    window.cookieconsent?.rejectAll?.();
    writeTemporaryHideState(false);
    setTemporarilyHidden(false);
    setBannerVisible(false);
    setSettingsOpen(false);
  };

  const handleSaveSettings = () => {
    window.cookieconsent?.savePreferences?.({
      analytics: analyticsEnabled,
      marketing: marketingEnabled,
    });
    writeTemporaryHideState(false);
    setTemporarilyHidden(false);
    setBannerVisible(false);
    setSettingsOpen(false);
  };

  const handleOpenSettings = () => {
    setSettingsOpen(true);
    const consent = window.cookieconsent?.getConsent?.();
    setAnalyticsEnabled(consent?.analytics === true);
    setMarketingEnabled(consent?.marketing === true);
  };

  const handleCloseSettings = () => {
    setSettingsOpen(false);
    // If consent already exists, hide banner when closing settings.
    if (window.cookieconsent?.hasConsented?.()) {
      writeTemporaryHideState(false);
      setTemporarilyHidden(false);
      setBannerVisible(false);
    } else if (temporarilyHidden) {
      setBannerVisible(false);
    }
  };

  const handleTemporaryHide = () => {
    writeTemporaryHideState(true);
    setTemporarilyHidden(true);
    setBannerVisible(false);
    setSettingsOpen(false);
  };

  const handleReopenBanner = () => {
    writeTemporaryHideState(false);
    setTemporarilyHidden(false);
    setBannerVisible(true);
    setSettingsOpen(true);

    const consent = window.cookieconsent?.getConsent?.();
    setAnalyticsEnabled(consent?.analytics === true);
    setMarketingEnabled(consent?.marketing === true);
  };

  if (!bannerVisible && !temporarilyHidden) {
    return null;
  }

  return (
    <>
      {settingsOpen ? <div className={css.overlay} onClick={handleCloseSettings} /> : null}

      {bannerVisible && settingsOpen ? (
        <div
          className={css.settingsModal}
          role="dialog"
          aria-label={intl.formatMessage({ id: 'CookieConsent.settingsTitle' })}
        >
          <div className={css.settingsHeader}>
            <h2 className={css.settingsTitle}>
              <FormattedMessage id="CookieConsent.settingsTitle" />
            </h2>
            <button
              type="button"
              className={css.closeButton}
              onClick={handleCloseSettings}
              aria-label={intl.formatMessage({ id: 'CookieConsent.close' })}
            >
              ✕
            </button>
          </div>
          <p className={css.settingsDescription}>
            <FormattedMessage id="CookieConsent.settingsDescription" />
          </p>

          <div className={css.preferenceRow}>
            <div className={css.preferenceInfo}>
              <span className={css.preferenceLabel}>
                <FormattedMessage id="CookieConsent.necessaryTitle" />
              </span>
              <span className={css.preferenceHelp}>
                <FormattedMessage id="CookieConsent.necessaryDescription" />
              </span>
            </div>
            <div className={css.toggleDisabled}>
              <FormattedMessage id="CookieConsent.alwaysOn" />
            </div>
          </div>

          <div className={css.preferenceRow}>
            <div className={css.preferenceInfo}>
              <span className={css.preferenceLabel}>
                <FormattedMessage id="CookieConsent.analyticsTitle" />
              </span>
              <span className={css.preferenceHelp}>
                <FormattedMessage id="CookieConsent.analyticsDescription" />
              </span>
            </div>
            <label className={css.toggleSwitch}>
              <input
                type="checkbox"
                checked={analyticsEnabled}
                onChange={e => setAnalyticsEnabled(e.target.checked)}
                className={css.toggleInput}
              />
              <span className={css.toggleSlider}></span>
            </label>
          </div>

          <div className={css.preferenceRow}>
            <div className={css.preferenceInfo}>
              <span className={css.preferenceLabel}>
                <FormattedMessage id="CookieConsent.marketingTitle" />
              </span>
              <span className={css.preferenceHelp}>
                <FormattedMessage id="CookieConsent.marketingDescription" />
              </span>
            </div>
            <label className={css.toggleSwitch}>
              <input
                type="checkbox"
                checked={marketingEnabled}
                onChange={e => setMarketingEnabled(e.target.checked)}
                className={css.toggleInput}
              />
              <span className={css.toggleSlider}></span>
            </label>
          </div>

          <div className={css.settingsActions}>
            <button type="button" className={css.rejectButton} onClick={handleRejectAll}>
              <FormattedMessage id="CookieConsent.reject" />
            </button>
            <button type="button" className={css.settingsButton} onClick={handleCloseSettings}>
              <FormattedMessage id="CookieConsent.cancel" />
            </button>
            <button type="button" className={css.acceptButton} onClick={handleSaveSettings}>
              <FormattedMessage id="CookieConsent.save" />
            </button>
          </div>
        </div>
      ) : bannerVisible ? (
        <div
          className={css.banner}
          role="dialog"
          aria-label={intl.formatMessage({ id: 'CookieConsent.ariaLabel' })}
        >
          <button
            type="button"
            className={css.mobileCloseButton}
            onClick={handleTemporaryHide}
            aria-label={intl.formatMessage({ id: 'CookieConsent.close' })}
          >
            ✕
          </button>
          <div className={css.bannerContent}>
            <h2 className={css.bannerTitle}>
              <FormattedMessage id="CookieConsent.title" />
            </h2>
            <p className={css.bannerDescription}>
              <FormattedMessage id="CookieConsent.description" />
            </p>
          </div>
          <div className={css.bannerActions}>
            <button type="button" className={css.rejectButton} onClick={handleRejectAll}>
              <FormattedMessage id="CookieConsent.reject" />
            </button>
            <button type="button" className={css.settingsButton} onClick={handleOpenSettings}>
              <FormattedMessage id="CookieConsent.settings" />
            </button>
            <button type="button" className={css.acceptButton} onClick={handleAcceptAll}>
              <FormattedMessage id="CookieConsent.accept" />
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default CookieConsentBanner;
